import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const InventoryTable = ({
  inventoryData,
  selectedItems = [],
  onSelectionChange,
  onBarcodeGenerate,
  onStockTransfer,
  onViewHistory
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Helper functions for selection
  const getAllPieceIds = () => {
    return inventoryData.flatMap(design =>
      design.sizes.flatMap(size =>
        size.pieces.map(piece => `${design.id}-${size.size}-${piece}`)
      )
    );
  };

  const getDesignPieceIds = (design) => {
    return design.sizes.flatMap(size =>
      size.pieces.map(piece => `${design.id}-${size.size}-${piece}`)
    );
  };

  const getSizePieceIds = (design, size) => {
    return size.pieces.map(piece => `${design.id}-${size.size}-${piece}`);
  };

  // Selection handlers
  const handleSelectAll = (checked) => {
    onSelectionChange(checked ? getAllPieceIds() : []);
  };

  const handleDesignSelect = (design, checked) => {
    const designPieceIds = getDesignPieceIds(design);
    if (checked) {
      onSelectionChange([...new Set([...selectedItems, ...designPieceIds])]);
    } else {
      onSelectionChange(selectedItems.filter(id => !designPieceIds.includes(id)));
    }
  };

  const handleSizeSelect = (design, size, checked) => {
    const sizePieceIds = getSizePieceIds(design, size);
    if (checked) {
      onSelectionChange([...new Set([...selectedItems, ...sizePieceIds])]);
    } else {
      onSelectionChange(selectedItems.filter(id => !sizePieceIds.includes(id)));
    }
  };

  const handlePieceSelect = (pieceId, checked) => {
    onSelectionChange(checked
      ? [...selectedItems, pieceId]
      : selectedItems.filter(id => id !== pieceId)
    );
  };

  // Selection state checkers
  const isDesignSelected = (design) => {
    const designPieceIds = getDesignPieceIds(design);
    return designPieceIds.length > 0 &&
      designPieceIds.every(id => selectedItems.includes(id));
  };

  const isDesignPartialSelected = (design) => {
    const designPieceIds = getDesignPieceIds(design);
    return designPieceIds.some(id => selectedItems.includes(id)) &&
      !designPieceIds.every(id => selectedItems.includes(id));
  };

  const isSizeSelected = (design, size) => {
    return getSizePieceIds(design, size).every(id => selectedItems.includes(id));
  };

  const isSizePartialSelected = (design, size) => {
    const sizePieceIds = getSizePieceIds(design, size);
    return sizePieceIds.some(id => selectedItems.includes(id)) &&
      !sizePieceIds.every(id => selectedItems.includes(id));
  };

  // Other utility functions
  const toggleRowExpansion = (designId) => {
    const newExpanded = new Set(expandedRows);
    newExpanded.has(designId) ? newExpanded.delete(designId) : newExpanded.add(designId);
    setExpandedRows(newExpanded);
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'in-stock': 'bg-success/10 text-success',
      'low-stock': 'bg-warning/10 text-warning',
      'out-of-stock': 'bg-error/10 text-error',
      'reserved': 'bg-primary/10 text-primary'
    };
    return statusColors[status] || 'bg-muted text-muted-foreground';
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return 'ArrowUpDown';
    return sortConfig.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
  <div className="overflow-x-auto sm:overflow-x-visible">
    <table className="w-full table-auto sm:table-fixed">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="p-4 text-left">
                <Checkbox
                  checked={selectedItems.length > 0 && selectedItems.length === getAllPieceIds().length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
              <th className="p-4 text-left">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('design')}
                  iconName={getSortIcon('design')}
                  iconPosition="right"
                  iconSize={14}
                  className="font-semibold text-foreground"
                >
                  Design
                </Button>
              </th>
              <th className="p-4 text-left">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('totalStock')}
                  iconName={getSortIcon('totalStock')}
                  iconPosition="right"
                  iconSize={14}
                  className="font-semibold text-foreground"
                >
                  Total Stock
                </Button>
              </th>
              <th className="p-4 text-left">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('reserved')}
                  iconName={getSortIcon('reserved')}
                  iconPosition="right"
                  iconSize={14}
                  className="font-semibold text-foreground"
                >
                  Reserved
                </Button>
              </th>
              <th className="p-4 text-left">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSort('available')}
                  iconName={getSortIcon('available')}
                  iconPosition="right"
                  iconSize={14}
                  className="font-semibold text-foreground"
                >
                  Available
                </Button>
              </th>
              <th className="p-4 text-left font-semibold text-foreground">Status</th>
              <th className="p-4 text-left font-semibold text-foreground">Location</th>
              <th className="p-4 text-left font-semibold text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {inventoryData.map((design) => [
              /* Design Row */
              <tr key={design.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                <td className="p-4">
                  <Checkbox
                    checked={isDesignSelected(design)}
                    indeterminate={isDesignPartialSelected(design)}
                    onChange={(e) => handleDesignSelect(design, e.target.checked)}
                  />
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleRowExpansion(design.id)}
                      iconName={expandedRows.has(design.id) ? "ChevronDown" : "ChevronRight"}
                      iconSize={16}
                    />
                    <div>
                      <p className="font-medium text-foreground">{design.name}</p>
                      <p className="text-sm text-muted-foreground">{design.category}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span className="font-semibold text-foreground">
                    {design.totalStock.toLocaleString('en-IN')}
                  </span>
                </td>
                <td className="p-4">
                  <span className="text-warning font-medium">
                    {design.reserved.toLocaleString('en-IN')}
                  </span>
                </td>
                <td className="p-4">
                  <span className="text-success font-medium">
                    {design.available.toLocaleString('en-IN')}
                  </span>
                </td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(design.status)}`}>
                    {design.status.replace('-', ' ').toUpperCase()}
                  </span>
                </td>
                <td className="p-4">
                  <span className="text-sm text-muted-foreground">{design.location}</span>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onBarcodeGenerate(design.id)}
                      iconName="QrCode"
                      iconSize={16}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onViewHistory(design.id)}
                      iconName="History"
                      iconSize={16}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onStockTransfer(design.id)}
                      iconName="ArrowRightLeft"
                      iconSize={16}
                    />
                  </div>
                </td>
              </tr>,

              /* Expanded Size Rows */
              ...(expandedRows.has(design.id)
                ? design.sizes.map((size) => (
                  <tr key={`${design.id}-${size.size}`} className="bg-muted/20 border-b border-border">
                    <td className="p-4 pl-12">
                      <Checkbox
                        checked={isSizeSelected(design, size)}
                        indeterminate={isSizePartialSelected(design, size)}
                        onChange={(e) => handleSizeSelect(design, size, e.target.checked)}
                      />
                    </td>
                    <td className="p-4 pl-12">
                      <div className="flex items-center space-x-2">
                        <span className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center text-xs font-medium text-secondary-foreground">
                          {size.size}
                        </span>
                        <span className="text-sm text-foreground">Size {size.size}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="font-medium text-foreground">
                        {size.totalPieces.toLocaleString('en-IN')}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-warning">
                        {size.reservedPieces.toLocaleString('en-IN')}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-success">
                        {size.availablePieces.toLocaleString('en-IN')}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(size.status)}`}>
                        {size.status.replace('-', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-muted-foreground">{size.location}</span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center space-x-2">
                        {size.barcode && (
                          <div className="flex items-center space-x-1">
                            <Icon name="QrCode" size={14} className="text-success" />
                            <span className="text-xs text-success font-mono">{size.barcode}</span>
                          </div>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onBarcodeGenerate(`${design.id}-${size.size}`)}
                          iconName="Printer"
                          iconSize={14}
                        />
                      </div>
                    </td>
                  </tr>
                ))
                : []
              )])}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InventoryTable;